/**
 * ft-select
 * A reusable select component with keyboard navigation and accessibility.
 *
 * Usage:
 *   Single select:
 *   <ft-select
 *     options='[{"value":"a","label":"Option A"},{"value":"b","label":"Option B"}]'
 *     value="a"
 *     placeholder="Select...">
 *   </ft-select>
 *
 *   Multi-select:
 *   <ft-select
 *     type="multiselect"
 *     options='[...]'
 *     value='["a","b"]'
 *     placeholder="Select items...">
 *   </ft-select>
 *
 *   Multi-select with star icon:
 *   <ft-select
 *     type="multiselect"
 *     icon="star"
 *     placeholder="Watchlist">
 *   </ft-select>
 *
 * Attributes:
 *   - icon: "check" (default) | "star" | "none" - Icon shown in multiselect trigger
 *
 * Events:
 *   - change: { value: string | string[], label: string }
 *
 * Public API:
 *   - getValue(): string | string[] | null
 *   - setValue(value: string | string[]): void
 *   - setOptions(options: Option[]): void
 *   - open(): void
 *   - close(): void
 *   - toggle(): void
 */

import { icon } from '../../../shared/icons.js';

interface Option {
  value: string;
  label: string;
}

class Select extends HTMLElement {
  private abortController = new AbortController();
  private sheet: CSSStyleSheet | null = null;
  private pending = false;
  private isOpen = false;
  private options: Option[] = [];
  private selectedValue: string | null = null;
  private selectedValues: Set<string> = new Set();
  private focusedIndex = -1;
  private readonly instanceId = Math.random().toString(36).slice(2, 8);

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["options", "value", "placeholder", "disabled", "align", "variant", "size", "type", "icon"];
  }

  private get isMultiselect(): boolean {
    return this.getAttribute("type") === "multiselect";
  }

  private get placeholder(): string {
    return this.getAttribute("placeholder") || "Select...";
  }

  private get align(): "left" | "right" {
    return (this.getAttribute("align") as "left" | "right") || "left";
  }

  private get variant(): "primary" | "secondary" {
    return (this.getAttribute("variant") as "primary" | "secondary") || "secondary";
  }

  private get size(): "small" | "normal" | "large" {
    return (this.getAttribute("size") as "small" | "normal" | "large") || "normal";
  }

  private get icon(): "check" | "star" | "none" {
    return (this.getAttribute("icon") as "check" | "star" | "none") || "check";
  }

  connectedCallback(): void {
    this.parseAttributes();
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.shadowRoot?.children.length) return;
    if (this.pending) return;

    this.pending = true;
    queueMicrotask(() => {
      this.pending = false;
      this.parseAttributes();
      this.updateDOM();
    });
  }

  private parseAttributes(): void {
    const optionsAttr = this.getAttribute("options");
    if (optionsAttr) {
      try {
        this.options = JSON.parse(optionsAttr);
      } catch {
        console.warn("ft-select: invalid options JSON");
      }
    }

    const valueAttr = this.getAttribute("value");
    if (this.isMultiselect && valueAttr) {
      try {
        this.selectedValues = new Set(JSON.parse(valueAttr));
      } catch {
        this.selectedValues = new Set();
      }
    } else {
      this.selectedValue = valueAttr;
    }
  }

  // Public API
  getValue(): string | string[] | null {
    return this.isMultiselect ? Array.from(this.selectedValues) : this.selectedValue;
  }

  setValue(value: string | string[]): void {
    if (this.isMultiselect && Array.isArray(value)) {
      this.selectedValues = new Set(value);
      this.setAttribute("value", JSON.stringify(value));
    } else if (typeof value === "string") {
      this.selectedValue = value;
      this.setAttribute("value", value);
    }
  }

  setOptions(options: Option[]): void {
    this.options = options;
    this.updateMenu();
  }

  open(): void {
    if (this.hasAttribute("disabled")) return;
    this.isOpen = true;
    this.classList.add("open");
    this.focusedIndex = this.isMultiselect
      ? this.options.findIndex(o => this.selectedValues.has(o.value))
      : this.options.findIndex(o => o.value === this.selectedValue);
    this.updateFocus();
    this.dispatchEvent(new CustomEvent("open"));
  }

  close(): void {
    this.isOpen = false;
    this.classList.remove("open");
    this.focusedIndex = -1;
    this.updateFocus();
    this.dispatchEvent(new CustomEvent("close"));
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private get currentLabel(): string {
    if (this.isMultiselect) return this.placeholder;
    if (!this.selectedValue) return this.placeholder;
    return this.options.find(o => o.value === this.selectedValue)?.label || this.selectedValue;
  }

  private get selectedCount(): number {
    return this.selectedValues.size;
  }

  private get hasSelection(): boolean {
    return this.isMultiselect ? this.selectedValues.size > 0 : !!this.selectedValue;
  }

  private render(): void {
    if (!this.shadowRoot) return;

    if (!this.sheet) {
      this.sheet = new CSSStyleSheet();
      this.sheet.replaceSync(this.getStyles());
      this.shadowRoot.adoptedStyleSheets = [this.sheet];
    }

    this.shadowRoot.innerHTML = this.getTemplate();
  }

  private getTemplate(): string {
    const showIcon = this.isMultiselect && this.icon !== "none";
    const showCount = this.isMultiselect && this.selectedCount > 0;

    return `
      <div class="container${this.hasSelection ? " has-selection" : ""}">
        <button
          class="trigger"
          type="button"
          aria-haspopup="listbox"
          aria-expanded="${this.isOpen}"
          aria-controls="menu-${this.instanceId}"
          ${this.hasAttribute("disabled") ? "disabled" : ""}>
          ${showIcon ? this.getIconSVG() : ""}
          <span class="label">${this.currentLabel}</span>
          ${showCount ? `<span class="count">${this.selectedCount}</span>` : ""}
          <span class="chevron" aria-hidden="true">${icon('chevron-down', 14)}</span>
        </button>
        <div class="menu" id="menu-${this.instanceId}" role="listbox" aria-label="Options">
          <slot name="header"></slot>
          <div class="menu-items">${this.getOptionsHTML()}</div>
          <slot name="footer"></slot>
        </div>
      </div>
    `;
  }

  private getIconSVG(): string {
    if (this.icon === "star") {
      return `<span class="trigger-icon icon-star" aria-hidden="true">${icon('star', 14)}</span>`;
    }
    return `<span class="trigger-icon icon-check" aria-hidden="true">${icon('check', 14)}</span>`;
  }

  private getOptionsHTML(): string {
    return this.options.map((opt, i) => {
      const isSelected = this.isMultiselect
        ? this.selectedValues.has(opt.value)
        : opt.value === this.selectedValue;

      return `
        <div
          class="item${isSelected ? " selected" : ""}${i === this.focusedIndex ? " focused" : ""}"
          role="option"
          id="opt-${this.instanceId}-${i}"
          aria-selected="${isSelected}"
          tabindex="-1"
          data-value="${opt.value}"
          data-index="${i}">
          ${this.isMultiselect ? `
            <span class="checkbox${isSelected ? " checked" : ""}">
              ${isSelected ? icon('check', 12) : ""}
            </span>
          ` : ""}
          <span class="item-label">${opt.label}</span>
        </div>
      `;
    }).join("");
  }

  private updateDOM(): void {
    this.updateTrigger();
    this.updateMenu();
  }

  private updateTrigger(): void {
    const container = this.shadowRoot?.querySelector(".container");
    const label = this.shadowRoot?.querySelector(".trigger .label");
    const count = this.shadowRoot?.querySelector(".count");

    if (container) {
      container.classList.toggle("has-selection", this.hasSelection);
    }
    if (label) {
      label.textContent = this.currentLabel;
    }
    if (count && this.isMultiselect) {
      count.textContent = String(this.selectedCount);
    }
  }

  private updateMenu(): void {
    const menuItems = this.shadowRoot?.querySelector(".menu-items");
    if (menuItems) {
      menuItems.innerHTML = this.getOptionsHTML();
    }
  }

  private updateFocus(): void {
    const items = this.shadowRoot?.querySelectorAll(".item");
    items?.forEach((item, i) => {
      item.classList.toggle("focused", i === this.focusedIndex);
    });

    const trigger = this.shadowRoot?.querySelector(".trigger");
    trigger?.setAttribute("aria-expanded", String(this.isOpen));

    if (this.isOpen && this.focusedIndex >= 0) {
      trigger?.setAttribute("aria-activedescendant", `opt-${this.instanceId}-${this.focusedIndex}`);
    } else {
      trigger?.removeAttribute("aria-activedescendant");
    }
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener("click", this.handleOutsideClick, { signal });
    document.addEventListener("keydown", this.handleKeydown, { signal });
    this.shadowRoot?.addEventListener("click", this.handleShadowClick, { signal });
  }

  private handleOutsideClick = (e: Event): void => {
    if (!e.composedPath().includes(this)) {
      this.close();
    }
  };

  private handleShadowClick = (e: Event): void => {
    const target = e.target as HTMLElement;

    if (target.closest(".trigger")) {
      this.toggle();
      return;
    }

    const item = target.closest(".item") as HTMLElement;
    if (item?.dataset.value) {
      const option = this.options.find(o => o.value === item.dataset.value);
      if (option) this.selectOption(option);
    }
  };

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this.isOpen && !this.contains(document.activeElement)) return;

    switch (e.key) {
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          this.close();
          this.shadowRoot?.querySelector<HTMLButtonElement>(".trigger")?.focus();
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          this.focusedIndex = Math.min(this.focusedIndex + 1, this.options.length - 1);
          this.updateFocus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (this.isOpen) {
          this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
          this.updateFocus();
        }
        break;

      case "Enter":
      case " ":
        if (this.isOpen && this.focusedIndex >= 0) {
          e.preventDefault();
          this.selectOption(this.options[this.focusedIndex]);
        } else if (!this.isOpen && this.contains(document.activeElement)) {
          e.preventDefault();
          this.open();
        }
        break;

      case "Tab":
        if (this.isOpen) this.close();
        break;
    }
  };

  private selectOption(option: Option): void {
    if (this.isMultiselect) {
      if (this.selectedValues.has(option.value)) {
        this.selectedValues.delete(option.value);
      } else {
        this.selectedValues.add(option.value);
      }
      const values = Array.from(this.selectedValues);
      this.setAttribute("value", JSON.stringify(values));

      this.dispatchEvent(new CustomEvent("change", {
        detail: { value: values, label: option.label },
        bubbles: true,
        composed: true,
      }));
    } else {
      this.selectedValue = option.value;
      this.setAttribute("value", option.value);
      this.close();

      this.dispatchEvent(new CustomEvent("change", {
        detail: { value: option.value, label: option.label },
        bubbles: true,
        composed: true,
      }));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: inline-block;
        position: relative;
        font-family: system-ui, -apple-system, sans-serif;
      }

      .container {
        position: relative;
      }

      .trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        cursor: pointer;
        text-align: left;
        border-radius: var(--radius, 6px);
        ${this.getSizeStyles()}
      }

      .trigger:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chevron {
        display: flex;
        flex-shrink: 0;
        opacity: 0.6;
        transition: transform 150ms;
      }

      :host(.open) .chevron {
        transform: rotate(180deg);
      }

      .menu {
        position: absolute;
        top: calc(100% + 4px);
        ${this.align === "right" ? "right: 0;" : "left: 0;"}
        min-width: 100%;
        max-height: 320px;
        overflow-x: hidden;
        overflow-y: auto;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition: opacity 150ms, transform 150ms, visibility 150ms;
        z-index: 100;
        border-radius: var(--radius, 6px);
      }

      :host(.open) .menu {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .item {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        text-align: left;
        cursor: pointer;
        border: none;
        background: none;
        transition: background 100ms;
      }

      .item:first-child { border-radius: 5px 5px 0 0; }
      .item:last-child { border-radius: 0 0 5px 5px; }
      .item:only-child { border-radius: 5px; }

      .item-label {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .checkbox {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border: 1.5px solid var(--input-border, #E2E4E8);
        border-radius: 3px;
        flex-shrink: 0;
      }

      .checkbox.checked {
        background: var(--success, #10b981);
        border-color: var(--success, #10b981);
        color: #fff;
      }

      .trigger-icon {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
      }

      .icon-check {
        color: var(--success, #10b981);
      }

      .icon-star {
        fill: var(--muted, #9ca3af);
        transition: fill 150ms;
      }

      .container.has-selection .icon-star {
        fill: var(--primary, #e0524d);
      }

      .count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        font-size: 11px;
        font-weight: 600;
        color: var(--success, #10b981);
        background: var(--success-bg, rgba(16, 185, 129, 0.1));
        border-radius: 9px;
      }

      .menu-items:empty { display: none; }

      ::slotted([slot="header"]) {
        display: block;
        padding: 8px 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--muted, #6b7280);
        border-bottom: 1px solid var(--input-border, #E2E4E8);
      }

      ::slotted([slot="footer"]) {
        display: block;
        padding: 8px 12px;
        font-size: 12px;
        color: var(--primary, #e0524d);
        border-top: 1px solid var(--input-border, #E2E4E8);
        cursor: pointer;
        background: transparent;
        border-left: none;
        border-right: none;
        border-bottom: none;
        width: 100%;
        text-align: left;
      }

      ::slotted([slot="footer"]:hover) {
        background: var(--tab-bg, #f4f4f5);
      }

      @media (prefers-reduced-motion: reduce) {
        .chevron, .menu, .item { transition: none; }
      }

      ${this.variant === "primary" ? this.getPrimaryStyles() : this.getSecondaryStyles()}
    `;
  }

  private getSizeStyles(): string {
    switch (this.size) {
      case "small":
        return "height: var(--height-sm, 32px); padding: 0 10px; font-size: 12px; font-weight: 500;";
      case "large":
        return "height: var(--height-lg, 48px); padding: 0 16px; font-size: 16px; font-weight: 500;";
      default:
        return "height: var(--height-md, 40px); padding: 0 12px; font-size: 14px; font-weight: 500;";
    }
  }

  private getItemSizeStyles(): string {
    switch (this.size) {
      case "small": return "padding: 6px 10px; font-size: 12px;";
      case "large": return "padding: 10px 16px; font-size: 16px;";
      default: return "padding: 8px 12px; font-size: 14px;";
    }
  }

  private getPrimaryStyles(): string {
    return `
      .trigger {
        color: #fff;
        background: #1a1a2e;
        border: 1px solid #333;
        transition: background 150ms;
      }
      .trigger:hover:not(:disabled) { background: #252540; }
      .trigger:focus {
        outline: none;
        background: #252540;
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
      }
      .menu {
        min-width: 140px;
        background: #1a1a2e;
        border: 1px solid #333;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }
      .item { ${this.getItemSizeStyles()} color: #ccc; }
      .item:hover, .item.focused { background: #252540; color: #fff; }
      .item.focused { background: #303050; }
      .item.selected { color: #10b981; font-weight: 600; }
    `;
  }

  private getSecondaryStyles(): string {
    return `
      .trigger {
        color: var(--text, #1C1C1E);
        background: var(--input-bg, #ffffff);
        border: 1px solid var(--input-border, #E2E4E8);
        transition: border-color 150ms, box-shadow 150ms;
      }
      .trigger:hover:not(:disabled) { border-color: var(--input-border-focus, #999999); }
      .trigger:focus {
        outline: none;
        border-color: var(--input-border-focus, #999999);
        box-shadow: 0 0 0 2px var(--focus-ring, rgba(0, 0, 0, 0.1));
      }
      .menu {
        background: var(--input-bg, #ffffff);
        border: 1px solid var(--input-border, #E2E4E8);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      .item { ${this.getItemSizeStyles()} color: var(--text, #1C1C1E); }
      .item:hover, .item.focused { background: var(--tab-bg, #f4f4f5); }
      .item.focused { background: var(--tab-bg-hover, #e9e9eb); }
      .item.selected { color: var(--primary, #e0524d); font-weight: 600; }
    `;
  }
}

if (!customElements.get("ft-select")) {
  customElements.define("ft-select", Select);
}
